import * as Sentry from '@sentry/node'
import { IJobInfo } from '../components/JobItem/type'
import { decrypt } from '../helpers/encode'
import { ELanguage } from '@microfrontends/react-components'
import { TJobInvitation } from '@microfrontends/react-components/components/ApplyJobs/types'

const API_GET_COMPANY_JOB_LIST = `${process.env.NEXT_PUBLIC_MS_BASE_PATH}/job-search/v1.0/search`
const SAVE_JOB_URL = `${process.env.NEXT_PUBLIC_MS_BASE_PATH}/api-gateway/v1.0/save-job`
const UNSAVE_JOB_URL = `${process.env.NEXT_PUBLIC_MS_BASE_PATH}/api-gateway/v1.0/unsave-job`
const API_SPECIAL_JOB_URL = `${process.env.NEXT_PUBLIC_MS_BASE_PATH}/api-gateway/v1.0/special-jobs`

export const DEFAULT_SUCCESS_CODE = 200
export const DEFAULT_ERROR_CODE = 400
export const DEFAULT_SUCCESS_MESSAGE = 'success'

export interface IJobListResponse {
    data?: IJobInfo[]
    facets?: {
        companies?: {}
        cityIds?: number[]
    }
    meta?: {
        code: number
        message: string
        nbHits?: number
        page?: number
        nbPages?: number
        hitsPerPage?: number
    }
}

export interface ISaveResponse {
    data?: {
        type: string
        id: number
    }
    meta?: {
        code?: number
        message?: string
    }
}
export interface ISaveJob {
    type: string
    id: number
    attributes: {
        applied: boolean
        matching: boolean
        saved: boolean
        userBlackList: boolean
        jobInvitation?: TJobInvitation
    }
}

export interface ISpecialJobResponse {
    data?: ISaveJob[]
    status: {
        code: number
        message: string
    }
}

export const getJobListFromCompany = async (
    accessToken: string,
    filter: object,
    page: number,
    query: string,
    userId?: number,
    hitsPerPage?: number,
    langCode?: ELanguage
): Promise<IJobListResponse> => {
    try {
        const response = await fetch(API_GET_COMPANY_JOB_LIST, {
            method: 'POST',
            headers: {
                'Accept-Language': langCode ?? ELanguage.VI,
                Authorization: `Bearer ${decrypt(accessToken)}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                query,
                filter,
                ranges: [],
                facets: 'workingLocations.cityId',
                order: [
                    {
                        field: 'approvedOn',
                        value: 'desc'
                    }
                ],
                hitsPerPage: hitsPerPage ? +hitsPerPage : 200,
                page: page,
                userId,
                retrieveFields: [
                    'address',
                    'benefits',
                    'jobTitle',
                    'isSalaryVisible',
                    'isShowLogo',
                    'companyLogo',
                    'userId',
                    'jobLevel',
                    'jobLevelId',
                    'jobLevelVI',
                    'jobId',
                    'companyId',
                    'approvedOn',
                    'isAnonymous',
                    'alias',
                    'expiredOn',
                    'industries',
                    'workingLocations',
                    'services',
                    'companyName',
                    'onlineOn',
                    'simpleServices',
                    'visibilityDisplay',
                    'isShowLogoInSearch',
                    'priorityOrder',
                    'skills',
                    'profilePublishedSiteMask',
                    'salary',
                    'salaryMin',
                    'salaryMax',
                    'prettySalary',
                    'jobDescription',
                    'jobRequirement'
                ]
            })
        })
        if (response.status === DEFAULT_SUCCESS_CODE) {
            return await response.json()
        }
    } catch (ex) {
        console.error(`[getJobListFromCompany] Error while trying to get job list`)
        Sentry.captureException(ex)
    }
    return {
        data: [],
        meta: {
            code: DEFAULT_ERROR_CODE,
            message: 'ERROR'
        }
    }
}

export const saveJob = async (jobId: number, token: string, language: string): Promise<ISaveResponse> => {
    try {
        const data = {
            jobId: jobId
        }

        const responseSaveJob = await fetch(SAVE_JOB_URL, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Accept-Language': language,
                'Content-Type': 'application/json',
                Authorization: `Bearer ${decrypt(token)}`
            },
            body: JSON.stringify(data)
        })

        if (responseSaveJob.status === DEFAULT_SUCCESS_CODE) {
            return await responseSaveJob.json()
        }
    } catch (ex) {
        console.error(`[SaveJobService] Error while trying to SAVE job or to convert the response to json`)
        Sentry.captureException(ex)
    }
    return {
        meta: {
            code: DEFAULT_ERROR_CODE,
            message: 'ERROR'
        }
    }
}

export const unSaveJob = async (jobId: number, token: string, language: string): Promise<ISaveResponse> => {
    try {
        const data = {
            jobId: jobId
        }
        const responseUnSaveJob = await fetch(UNSAVE_JOB_URL, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Accept-Language': language,
                'Content-Type': 'application/json',
                Authorization: `Bearer ${decrypt(token)}`
            },
            body: JSON.stringify(data)
        })
        if (responseUnSaveJob.status === DEFAULT_SUCCESS_CODE) {
            return await responseUnSaveJob.json()
        }
    } catch (ex) {
        console.error(`[SaveJobService] Error while trying to UN-SAVE job or to convert the response to json`)
        Sentry.captureException(ex)
    }
    return {
        meta: {
            code: DEFAULT_ERROR_CODE,
            message: 'ERROR'
        }
    }
}

export const getSpecialJob = async (listJobId: number[], token: string): Promise<ISpecialJobResponse> => {
    try {
        const decodeToken = decrypt(token)
        const responseSpecialJob = await fetch(API_SPECIAL_JOB_URL, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${decodeToken}`
            },
            body: JSON.stringify({
                jobId: listJobId
            })
        })
        if (responseSpecialJob.status === DEFAULT_SUCCESS_CODE) {
            return await responseSpecialJob.json()
        }
    } catch (ex) {
        console.error('GET Special Job ERROR', ex)
    }
    return {
        status: {
            code: DEFAULT_ERROR_CODE,
            message: 'ERROR'
        }
    }
}
